import React, { useEffect, useState } from 'react';
import { useToast, Toast, ToastType } from '../../contexts/ToastContext';
// ── Icons ────────────────────────────────────────────────────────────────────
function IconSuccess() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5530">
      
      <path d="M20 6 9 17l-5-5" data-id="element-5531" />
    </svg>;
}
function IconError() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5532">
      
      <circle cx="12" cy="12" r="10" data-id="element-5533" />
      <line x1="15" y1="9" x2="9" y2="15" data-id="element-5534" />
      <line x1="9" y1="9" x2="15" y2="15" data-id="element-5535" />
    </svg>;
}
function IconWarning() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5536">
      
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" data-id="element-5537" />
      <line x1="12" y1="9" x2="12" y2="13" data-id="element-5538" />
      <line x1="12" y1="17" x2="12.01" y2="17" data-id="element-5539" />
    </svg>;
}
function IconInfo() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5540">
      
      <circle cx="12" cy="12" r="10" data-id="element-5541" />
      <line x1="12" y1="8" x2="12" y2="12" data-id="element-5542" />
      <line x1="12" y1="16" x2="12.01" y2="16" data-id="element-5543" />
    </svg>;
}
// ── Config per type ──────────────────────────────────────────────────────────
const CONFIG: Record<ToastType, {
  icon: React.ReactNode;
  classes: string;
  bar: string;
}> = {
  success: {
    icon: <IconSuccess data-id="element-5544" />,
    classes: 'bg-white dark:bg-gray-800 border-l-4 border-emerald-500 text-emerald-700 dark:text-emerald-400',
    bar: 'bg-emerald-500'
  },
  error: {
    icon: <IconError data-id="element-5545" />,
    classes: 'bg-white dark:bg-gray-800 border-l-4 border-red-500 text-red-700 dark:text-red-400',
    bar: 'bg-red-500'
  },
  warning: {
    icon: <IconWarning data-id="element-5546" />,
    classes: 'bg-white dark:bg-gray-800 border-l-4 border-amber-500 text-amber-700 dark:text-amber-400',
    bar: 'bg-amber-500'
  },
  info: {
    icon: <IconInfo data-id="element-5547" />,
    classes: 'bg-white dark:bg-gray-800 border-l-4 border-teal-500 text-teal-700 dark:text-teal-400',
    bar: 'bg-teal-500'
  }
};
// ── Single toast item ────────────────────────────────────────────────────────
function ToastItem({
  toast,
  onDismiss
}: {
  toast: Toast;
  onDismiss: (id: string) => void;
}) {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const duration = toast.duration ?? 3500;
  const cfg = CONFIG[toast.type];
  // Mount animation
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);
  const handleDismiss = () => {
    setLeaving(true);
    setTimeout(() => onDismiss(toast.id), 300);
  };
  return <div role="alert" onClick={handleDismiss} style={{
    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    transform: visible && !leaving ? 'translateX(0) scale(1)' : 'translateX(110%) scale(0.92)',
    opacity: visible && !leaving ? 1 : 0
  }} className={`
        relative flex items-start gap-3 w-80 max-w-[calc(100vw-2rem)]
        rounded-xl shadow-lg shadow-black/10 dark:shadow-black/30
        px-4 py-3 cursor-pointer select-none
        overflow-hidden
        ${cfg.classes}
      `} data-id="element-5548">
      
      {/* Icon */}
      <span className="mt-0.5 shrink-0" data-id="element-5549">{cfg.icon}</span>

      {/* Text */}
      <div className="flex-1 min-w-0" data-id="element-5550">
        <p className="font-semibold text-sm leading-snug text-gray-900 dark:text-white" data-id="element-5551">
          {toast.title}
        </p>
        {toast.message && <p className="text-xs mt-0.5 text-gray-500 dark:text-gray-400 leading-relaxed" data-id="element-5552">
            {toast.message}
          </p>}
      </div>

      {/* Close button */}
      <button onClick={e => {
      e.stopPropagation();
      handleDismiss();
    }} className="shrink-0 mt-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors" aria-label="Cerrar" data-id="element-5553">
        
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" data-id="element-5554">
          
          <line x1="18" y1="6" x2="6" y2="18" data-id="element-5555" />
          <line x1="6" y1="6" x2="18" y2="18" data-id="element-5556" />
        </svg>
      </button>

      {/* Progress bar */}
      <span className={`absolute bottom-0 left-0 h-0.5 ${cfg.bar} opacity-60`} style={{
      animation: `toast-progress ${duration}ms linear forwards`
    }} data-id="element-5557" />
      

      <style data-id="element-5558">{`
        @keyframes toast-progress {
          from { width: 100%; }
          to   { width: 0%;   }
        }
      `}</style>
    </div>;
}
// ── Container ────────────────────────────────────────────────────────────────
export function ToastContainer() {
  const {
    toasts,
    dismissToast
  } = useToast();
  return <div aria-live="polite" className="fixed bottom-5 right-5 z-[99999] flex flex-col gap-2 items-end pointer-events-none" data-id="element-5559">
      
      {toasts.map(t => <div key={t.id} className="pointer-events-auto" data-id="element-5560">
          <ToastItem toast={t} onDismiss={dismissToast} data-id="element-5561" />
        </div>)}
    </div>;
}